import AsyncStorage from '@react-native-async-storage/async-storage';

import { SecureStorage, SecureStorageKeys } from '../storage/SecureStorage';
import { createLogger } from '../../utils/logger';

const logger = createLogger('tokenStorage');

/**
 * The AsyncStorage keys used by older versions of the app. On first launch
 * post-keychain-migration these get drained into SecureStorage and then
 * deleted. Listed here so the logout wipe can also scrub them.
 */
export const LEGACY_TOKEN_KEYS = [
  'token',
  '@auth_token',
  '@refresh_token',
  '@device_trust_token',
] as const;

let migrationPromise: Promise<void> | null = null;

/**
 * On first call, pull any legacy tokens from AsyncStorage into the keychain
 * and wipe the plaintext copies. Idempotent and safe to call many times —
 * subsequent calls return the same resolved promise.
 */
export function migrateLegacyTokensIfNeeded(): Promise<void> {
  if (!migrationPromise) {
    migrationPromise = (async () => {
      try {
        const [legacyAuth, legacyRefresh, legacyTrust] = await Promise.all([
          AsyncStorage.getItem('@auth_token'),
          AsyncStorage.getItem('@refresh_token'),
          AsyncStorage.getItem('@device_trust_token'),
        ]);
        // `token` and `@auth_token` hold the same JWT in the legacy layout.
        // If only one is set, honor whichever we find.
        const legacyToken =
          legacyAuth || (await AsyncStorage.getItem('token'));

        const writes: Promise<unknown>[] = [];
        if (legacyToken) {
          writes.push(
            SecureStorage.setItem(SecureStorageKeys.authToken, legacyToken),
          );
        }
        if (legacyRefresh) {
          writes.push(
            SecureStorage.setItem(
              SecureStorageKeys.refreshToken,
              legacyRefresh,
            ),
          );
        }
        if (legacyTrust) {
          writes.push(
            SecureStorage.setItem(
              SecureStorageKeys.deviceTrustToken,
              legacyTrust,
            ),
          );
        }
        if (writes.length > 0) {
          await Promise.all(writes);
          logger.info('Migrated legacy auth tokens from AsyncStorage to keychain');
        }

        // Always scrub — idempotent for new installs (nothing to remove).
        await AsyncStorage.multiRemove([...LEGACY_TOKEN_KEYS]);
      } catch (err) {
        // Never let migration failure block login. Reset the promise so a
        // future call can retry; readers fall through to SecureStorage which
        // may still have a valid value from an earlier run.
        migrationPromise = null;
        logger.error('Legacy token migration failed');
        throw err;
      }
    })();
  }
  return migrationPromise;
}

export async function getAuthToken(): Promise<string | null> {
  await migrateLegacyTokensIfNeeded();
  return SecureStorage.getItem(SecureStorageKeys.authToken);
}

export async function setAuthToken(token: string): Promise<void> {
  await SecureStorage.setItem(SecureStorageKeys.authToken, token);
}

export async function removeAuthToken(): Promise<void> {
  await SecureStorage.removeItem(SecureStorageKeys.authToken);
}

export async function getRefreshToken(): Promise<string | null> {
  await migrateLegacyTokensIfNeeded();
  return SecureStorage.getItem(SecureStorageKeys.refreshToken);
}

export async function setRefreshToken(token: string): Promise<void> {
  await SecureStorage.setItem(SecureStorageKeys.refreshToken, token);
}

export async function removeRefreshToken(): Promise<void> {
  await SecureStorage.removeItem(SecureStorageKeys.refreshToken);
}

export async function getDeviceTrustToken(): Promise<string | null> {
  await migrateLegacyTokensIfNeeded();
  return SecureStorage.getItem(SecureStorageKeys.deviceTrustToken);
}

export async function setDeviceTrustToken(token: string): Promise<void> {
  await SecureStorage.setItem(SecureStorageKeys.deviceTrustToken, token);
}

export async function removeDeviceTrustToken(): Promise<void> {
  await SecureStorage.removeItem(SecureStorageKeys.deviceTrustToken);
}

/**
 * Remove every auth token (keychain + any remaining AsyncStorage scraps).
 * Called on logout and 401 auto-logout.
 */
export async function removeAllTokens(options: { keepDeviceTrust?: boolean } = {}): Promise<void> {
  await Promise.all([
    SecureStorage.removeItem(SecureStorageKeys.authToken),
    SecureStorage.removeItem(SecureStorageKeys.refreshToken),
    options.keepDeviceTrust
      ? Promise.resolve()
      : SecureStorage.removeItem(SecureStorageKeys.deviceTrustToken),
    AsyncStorage.multiRemove([...LEGACY_TOKEN_KEYS]),
  ]);
}

/**
 * Test-only: resets the module's migration memo so the next getAuthToken
 * call re-runs the AsyncStorage drain. Must not be called from production code.
 */
export function __resetTokenStorageForTests(): void {
  migrationPromise = null;
}
